import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import toInteger from "lodash/toInteger";
import toString from "lodash/toString";
import { IApplicationSearchItem } from "models/Application.model";
import { Cradle } from "services/serviceContainer.types";
import { ServiceError, ServiceErrorCode } from "services/ServiceError";
import { IApplicationSearchService, SearchApplicationsForPageArgs } from "./ApplicationSearchService.type";
import { ApplicationStatusUtil } from "models/ApplicationStatus.model";
import { parseParticipantAccessLevelString } from "models/Participant.model";

export class ApplicationSearchService implements IApplicationSearchService {
  private readonly apiClient: Cradle["apiClient"];
  private readonly i18n: Cradle["i18n"];

  constructor(args: Pick<Cradle, "apiClient" | "i18n">) {
    this.apiClient = args.apiClient;
    this.i18n = args.i18n;
  }

  public async searchApplications(args: SearchApplicationsForPageArgs) {
    const { pageIndex, pageSize, address, applicationNumber, authorities } = args;

    const queryParams = new URLSearchParams();

    queryParams.set("pageIndex", toString(pageIndex));
    queryParams.set("pageSize", toString(pageSize));

    if (authorities && !isEmpty(authorities)) {
      queryParams.set("authorities", authorities.join(","));
    }

    if (address) {
      queryParams.set("address", toString(address));
    }

    if (applicationNumber) {
      queryParams.set("applicationNumber", toString(applicationNumber));
    }

    const response = await this.apiClient.protectedApi.get(`/user/applications-search?${queryParams.toString()}`);

    const jsonArr = response.data;
    if (!Array.isArray(jsonArr)) {
      throw new ServiceError(ServiceErrorCode.ServerError, this.i18n.t(`Failed to fetch access requests`));
    }

    return jsonArr.map(this.parseApplicationSearchItem);
  }

  private parseApplicationSearchItem(json: any): IApplicationSearchItem {
    return {
      address: toString(get(json, "address")),
      applicationId: toInteger(get(json, "applicationId")),
      applicationNumber: toString(get(json, "applicationNumber")),
      consentNumber: toString(get(json, "consentNumber")),
      formDisplayName: toString(get(json, "formDisplayName")),
      status: ApplicationStatusUtil.parse(toString(get(json, "status"))),
      authorityDisplayName: toString(get(json, "authorityDisplayName")),
      accessRequestedDate: toString(get(json, "accessRequestedDate")),
      accessLevel: parseParticipantAccessLevelString(toString(get(json, "accessLevel"))),
      hasChildApplications: Boolean(get(json, "hasChildApplications")),
    };
  }
}
